import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  evaluateAnswer,
  followUpFor,
  describeRequirements,
  newId,
  EvaluationError,
  type AnswerPayload,
  type RunnableChecklist,
  type RunnableItem,
} from '@odr/shared';

import { ApiError, api } from '../../lib/api';
import * as store from '../../lib/offline/db';
import { queue, queuePhoto, flush } from '../../lib/offline/sync';

/**
 * Checklist run state.
 *
 * Offline-first by construction: the cached copy in IndexedDB is what the UI
 * renders, every answer is written there first, and the network is a
 * best-effort background concern. The operator never waits on a request to see
 * their own tap register.
 */

export interface LocalAnswer extends AnswerPayload {
  /** Local photo ids not yet uploaded, so evidence counts work while offline. */
  pendingPhotoIds: string[];
}

export interface RunState {
  runnable: RunnableChecklist | null;
  answers: Map<string, LocalAnswer>;
  /** Item ids that already have a Problem Tag (server-side or queued locally). */
  taggedItems: Map<string, string>;
  loading: boolean;
  error: string | null;
  /** True when we are showing a cached copy because the server is unreachable. */
  fromCache: boolean;
}

export function flattenItems(runnable: RunnableChecklist | null): RunnableItem[] {
  return runnable?.sections.flatMap((section) => section.items) ?? [];
}

export function useRun(runId: string) {
  const [state, setState] = useState<RunState>({
    runnable: null,
    answers: new Map(),
    taggedItems: new Map(),
    loading: true,
    error: null,
    fromCache: false,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));

    // Show the cached copy immediately; refresh from the server behind it.
    const cached = await store.getCachedRun(runId);
    if (cached) {
      const hydrated = await hydrate(cached, runId);
      setState((s) => ({
        ...s,
        runnable: cached,
        answers: hydrated.answers,
        taggedItems: hydrated.taggedItems,
        loading: false,
        fromCache: true,
      }));
    }

    try {
      const fresh = await api.get<RunnableChecklist>(`/api/checklist-runs/${runId}`);
      await store.cacheRun(fresh);
      const hydrated = await hydrate(fresh, runId);
      setState({
        runnable: fresh,
        answers: hydrated.answers,
        taggedItems: hydrated.taggedItems,
        loading: false,
        error: null,
        fromCache: false,
      });
    } catch (error) {
      if (cached) {
        // Offline with a cached copy is a completely normal working state.
        setState((s) => ({ ...s, loading: false, fromCache: true }));
        return;
      }
      setState((s) => ({
        ...s,
        loading: false,
        error:
          error instanceof ApiError && error.isNetworkError
            ? 'This checklist is not saved on this device yet, and there is no connection. Open it once while online to be able to work offline.'
            : error instanceof ApiError
              ? error.message
              : 'Could not open this checklist.',
      }));
    }
  }, [runId]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = useMemo(() => flattenItems(state.runnable), [state.runnable]);

  /** Evidence count for an item = uploaded media + photos still in the outbox. */
  const evidenceCount = useCallback(
    (itemId: string) => {
      const answer = state.answers.get(itemId);
      if (!answer) return 0;
      return (answer.evidenceIds?.length ?? 0) + answer.pendingPhotoIds.length;
    },
    [state.answers],
  );

  /**
   * Record an answer.
   *
   * Judged locally with the shared evaluation engine so the operator sees
   * "4.2 bar is below the minimum of 5 bar" instantly, offline. The server
   * re-evaluates on sync and is the authority — but it reaches the same verdict
   * because it runs the identical code.
   */
  const answer = useCallback(
    async (
      item: RunnableItem,
      value: {
        valueText?: string | null;
        valueNumber?: number | null;
        valueBool?: boolean | null;
        valueList?: string[] | null;
        markedNotApplicable?: boolean;
        comment?: string | null;
      },
    ) => {
      const existing = state.answers.get(item.id);
      const raw = {
        ...value,
        comment: value.comment ?? existing?.comment ?? null,
        evidenceCount: evidenceCount(item.id),
      };

      let evaluation;
      try {
        evaluation = evaluateAnswer(item, raw);
      } catch (error) {
        if (error instanceof EvaluationError) return { error: error.message };
        throw error;
      }

      const answerId = existing?.id ?? newId();
      const local: LocalAnswer = {
        id: answerId,
        itemId: item.id,
        valueText: value.valueText ?? null,
        valueNumber: value.valueNumber ?? null,
        valueBool: value.valueBool ?? null,
        valueList: value.valueList ?? null,
        result: evaluation.result,
        comment: raw.comment,
        answeredAt: new Date().toISOString(),
        evidenceIds: existing?.evidenceIds ?? [],
        pendingPhotoIds: existing?.pendingPhotoIds ?? [],
      };

      setState((s) => {
        const answers = new Map(s.answers);
        answers.set(item.id, local);
        return { ...s, answers };
      });

      await queue('run.answer', answerId, {
        runId,
        itemId: item.id,
        valueText: local.valueText,
        valueNumber: local.valueNumber,
        valueBool: local.valueBool,
        valueList: local.valueList,
        markedNotApplicable: value.markedNotApplicable,
        comment: local.comment,
        answeredAt: local.answeredAt,
      });

      const followUp = followUpFor(item, evaluation, raw);

      return {
        evaluation,
        outstanding: describeRequirements(followUp),
        needsProblemTag: followUp.needsProblemTag,
        blocks: followUp.blocks,
      };
    },
    [runId, state.answers, evidenceCount],
  );

  const attachPhoto = useCallback(
    async (item: RunnableItem, file: Blob, filename: string) => {
      const existing = state.answers.get(item.id);
      const photoId = newId();

      await queuePhoto({
        id: photoId,
        runId,
        answerId: existing?.id ?? null,
        blob: file,
        filename,
        mimeType: file.type || 'image/jpeg',
      });

      setState((s) => {
        const answers = new Map(s.answers);
        const current = answers.get(item.id);
        if (current) {
          answers.set(item.id, {
            ...current,
            pendingPhotoIds: [...current.pendingPhotoIds, photoId],
          });
        }
        return { ...s, answers };
      });

      return photoId;
    },
    [runId, state.answers],
  );

  /** Per-item outstanding obligations, recomputed from current local state. */
  const outstandingFor = useCallback(
    (item: RunnableItem): string[] => {
      const local = state.answers.get(item.id);
      if (!local) return [];
      const isAbnormal = local.result === 'FAIL' || local.result === 'ABNORMAL';
      const followUp = followUpFor(
        item,
        { result: local.result, isAbnormal, reason: null },
        { comment: local.comment, evidenceCount: evidenceCount(item.id) },
      );
      // A tag already raised for this item — server-side or still queued —
      // discharges the PROBLEM_TAG obligation.
      if (state.taggedItems.has(item.id)) followUp.needsProblemTag = false;
      return describeRequirements(followUp);
    },
    [state.answers, state.taggedItems, evidenceCount],
  );

  /** Raise a Problem Tag for an item. Works offline; queued like everything else. */
  const raiseProblem = useCallback(
    async (
      item: RunnableItem,
      input: { title: string; description: string; severity: string; safetyImpact: boolean; qualityImpact: boolean; productionImpact: boolean },
    ) => {
      const machineId = state.runnable?.machine.id;
      if (!machineId) throw new Error('Run not loaded');
      const answer = state.answers.get(item.id);
      const problemId = newId();

      await queue('problem.create', problemId, {
        machineId,
        componentId: item.componentId,
        runId,
        itemId: item.id,
        answerId: answer?.id ?? null,
        title: input.title,
        description: input.description,
        category: item.ciltType,
        severity: input.severity,
        safetyImpact: input.safetyImpact,
        qualityImpact: input.qualityImpact,
        productionImpact: input.productionImpact,
        detectedAt: new Date().toISOString(),
      });

      setState((s) => {
        const taggedItems = new Map(s.taggedItems);
        // Queued locally: the human-readable tag number is allocated server-side,
        // so show "pending" until it syncs rather than inventing one.
        taggedItems.set(item.id, 'pending');
        return { ...s, taggedItems };
      });

      return problemId;
    },
    [runId, state.runnable, state.answers],
  );

  const progress = useMemo(() => {
    const required = items.filter((i) => i.isRequired);
    const answered = items.filter((i) => state.answers.has(i.id));
    const abnormal = [...state.answers.values()].filter(
      (a) => a.result === 'FAIL' || a.result === 'ABNORMAL',
    );
    const unresolved = items.filter((item) => outstandingFor(item).length > 0);

    return {
      total: items.length,
      answered: answered.length,
      requiredRemaining: required.filter((i) => !state.answers.has(i.id)).length,
      abnormal: abnormal.length,
      unresolved,
      canSubmit:
        required.every((i) => state.answers.has(i.id)) && unresolved.length === 0,
    };
  }, [items, state.answers, outstandingFor]);

  const submit = useCallback(
    async (notes: string | null, signedName: string | null) => {
      await queue('run.submit', runId, { notes, signedName });
      // Best-effort immediate send so the operator gets confirmation if online.
      await flush();
    },
    [runId],
  );

  return {
    ...state,
    items,
    answer,
    attachPhoto,
    raiseProblem,
    outstandingFor,
    evidenceCount,
    progress,
    submit,
    reload: load,
  };
}

async function hydrate(
  runnable: RunnableChecklist,
  runId: string,
): Promise<{ answers: Map<string, LocalAnswer>; taggedItems: Map<string, string> }> {
  const map = new Map<string, LocalAnswer>();
  const taggedItems = new Map<string, string>();

  for (const answer of runnable.answers) {
    map.set(answer.itemId, { ...answer, pendingPhotoIds: [] });
  }

  for (const problem of runnable.problems ?? []) {
    if (problem.itemId) taggedItems.set(problem.itemId, problem.tagNo);
  }

  // Replay anything still in the outbox on top of the server's copy, so a
  // reload while offline does not appear to lose the operator's work.
  const queued = await store.queuedOperations();

  // Problem tags still in the outbox count too — the obligation is discharged
  // from the operator's point of view the moment they raise the tag.
  for (const op of queued) {
    if (op.type !== 'problem.create') continue;
    const payload = op.payload as { runId?: string; itemId?: string | null };
    if (payload.runId === runId && payload.itemId) {
      taggedItems.set(payload.itemId, 'pending');
    }
  }

  for (const op of queued) {
    if (op.type !== 'run.answer') continue;
    const payload = op.payload as {
      runId: string;
      itemId: string;
      valueText?: string | null;
      valueNumber?: number | null;
      valueBool?: boolean | null;
      valueList?: string[] | null;
      comment?: string | null;
      answeredAt?: string;
    };
    if (payload.runId !== runId) continue;

    const item = flattenItems(runnable).find((i) => i.id === payload.itemId);
    if (!item) continue;

    try {
      const evaluation = evaluateAnswer(item, payload);
      const existing = map.get(payload.itemId);
      map.set(payload.itemId, {
        id: op.entityId,
        itemId: payload.itemId,
        valueText: payload.valueText ?? null,
        valueNumber: payload.valueNumber ?? null,
        valueBool: payload.valueBool ?? null,
        valueList: payload.valueList ?? null,
        result: evaluation.result,
        comment: payload.comment ?? null,
        answeredAt: payload.answeredAt ?? op.clientTimestamp,
        evidenceIds: existing?.evidenceIds ?? [],
        pendingPhotoIds: existing?.pendingPhotoIds ?? [],
      });
    } catch {
      // A queued answer that no longer evaluates (template changed under us)
      // is left to the server to reject with a proper conflict message.
    }
  }

  // Attach any photos still waiting to upload.
  const photos = await store.photosForRun(runId);
  for (const photo of photos.filter((p) => !p.uploaded)) {
    for (const [itemId, answer] of map) {
      if (answer.id === photo.answerId) {
        map.set(itemId, {
          ...answer,
          pendingPhotoIds: [...answer.pendingPhotoIds, photo.id],
        });
      }
    }
  }

  return { answers: map, taggedItems };
}
